import React from 'react';
import Modal, { ModalBody, ModalHeader, ModalTitle, ModalFooter } from '../../../components/bootstrap/Modal';
import Button from '../../../components/bootstrap/Button';
import Icon from '../../../components/icon/Icon';

interface LocationDetailsModalProps {
	isOpen: boolean;
	setIsOpen: (isOpen: boolean) => void;
	location: any;
}

const LocationDetailsModal: React.FC<LocationDetailsModalProps> = ({ isOpen, setIsOpen, location }) => {
	if (!location) return null;

	return (
		<Modal isOpen={isOpen} setIsOpen={setIsOpen} size='lg' isScrollable>
			<ModalHeader setIsOpen={setIsOpen}>
				<ModalTitle id='location-modal-title' className='d-flex align-items-center gap-2'>
					<Icon icon='LocationOn' className='text-primary' size='lg' />
					Location Details
				</ModalTitle>
			</ModalHeader>
			<ModalBody className='p-0'>
				<div className="location-details-container">
					<div className="location-header bg-primary text-white">
						<div className="location-header-content">
							<div className="location-main-info">
								<div className="location-badge bg-light text-dark">
									<Icon icon="Store" className="me-2" />
									{location.locationType?.toUpperCase() || 'LOCATION'}
								</div>
								<h3 className="location-name mb-1 text-white">{location.locationName}</h3>
								<div className="location-code bg-light text-dark">
									<Icon icon="QrCode" className="me-1" />
									{location.locationCode}
								</div>
							</div>
							<div className="location-status">
								<div className={`status-indicator ${location.locationStatus === 'active' ? 'bg-success' : 'bg-danger'} text-white`}>
									<div className="status-dot"></div>
									{location.locationStatus?.toUpperCase()}
								</div>
							</div>
						</div>
					</div>

					<div className="location-details-grid p-4 bg-light">
						<div className="detail-card bg-white border">
							<div className="detail-card-header border-bottom border-light">
								<Icon icon="Phone" className="text-success" />
								<span>Contact Information</span>
							</div>
							<div className="detail-items">
								<div className="detail-item">
									<span className="detail-label text-muted">Primary Phone</span>
									<span className="detail-value text-dark">{location.phone1 || 'Not provided'}</span>
								</div>
								{location.phone2 && (
									<div className="detail-item">
										<span className="detail-label text-muted">Secondary Phone</span>
										<span className="detail-value text-dark">{location.phone2}</span>
									</div>
								)}
							</div>
						</div>

						<div className="detail-card bg-white border">
							<div className="detail-card-header border-bottom border-light">
								<Icon icon="Place" className="text-info" />
								<span>Address Information</span>
							</div>
							<div className="detail-items">
								<div className="detail-item">
									<span className="detail-label text-muted">Address</span>
									<span className="detail-value text-dark">
										{location.address1}
										{location.address2 && `, ${location.address2}`}
									</span>
								</div>
								<div className="detail-item">
									<span className="detail-label text-muted">City</span>
									<span className="detail-value text-dark">{location.city}</span>
								</div>
								<div className="detail-item">
									<span className="detail-label text-muted">Province</span>
									<span className="detail-value text-dark">{location.province}</span>
								</div>
								<div className="detail-item">
									<span className="detail-label text-muted">Country</span>
									<span className="detail-value text-dark">{location.country}</span>
								</div>
								{location.zip && (
									<div className="detail-item">
										<span className="detail-label text-muted">ZIP Code</span>
										<span className="detail-value text-dark">{location.zip}</span>
									</div>
								)}
							</div>
						</div>

						{location.locationManagerName && (
							<div className="detail-card bg-white border">
								<div className="detail-card-header border-bottom border-light">
									<Icon icon="PersonOutline" className="text-warning" />
									<span>Manager Information</span>
								</div>
								<div className="detail-items">
									<div className="detail-item">
										<span className="detail-label text-muted">Manager Name</span>
										<span className="detail-value text-dark">{location.locationManagerName}</span>
									</div>
									{location.locationManagerEmail && (
										<div className="detail-item">
											<span className="detail-label text-muted">Email</span>
											<span className="detail-value text-dark">{location.locationManagerEmail}</span>
										</div>
									)}
									{location.locationManagerPhone && (
										<div className="detail-item">
											<span className="detail-label text-muted">Phone</span>
											<span className="detail-value text-dark">{location.locationManagerPhone}</span>
										</div>
									)}
								</div>
							</div>
						)}

						<div className="detail-card bg-white border">
							<div className="detail-card-header border-bottom border-light">
								<Icon icon="AccountBalance" className="text-primary" />
								<span>Financial Information</span>
							</div>
							<div className="detail-items">
								<div className="detail-item">
									<span className="detail-label text-muted">Current Balance</span>
									<span className="detail-value text-dark">
										<span className="balance-amount text-success fw-bold">
											{location.locationBalance?.balance ? 
												`${location.currency} ${Number(location.locationBalance.balance).toLocaleString()}` : 
												'Not available'
											}
										</span>
									</span>
								</div>
								<div className="detail-item">
									<span className="detail-label text-muted">Currency</span>
									<span className="detail-value text-dark">{location.currency}</span>
								</div>
								<div className="detail-item">
									<span className="detail-label text-muted">Location Level</span>
									<span className="detail-value text-dark">Level {location.locationLevel}</span>
								</div>
								<div className="detail-item">
									<span className="detail-label text-muted">VAT Registered</span>
									<span className="detail-value">
										{location.vat ? (
											<span className="text-success"><Icon icon="CheckCircle" className="me-1" />Yes</span>
										) : (
											<span className="text-muted"><Icon icon="Cancel" className="me-1" />No</span>
										)}
									</span>
								</div>
							</div>
						</div>
					</div>

					{/* <div className="location-timestamps bg-light border-top p-3">
						<div className="timestamp-item">
							<Icon icon="Schedule" className="text-muted me-2" />
							<span className="text-muted">Created: {new Date(location.createdAt).toLocaleString()}</span>
						</div>
						<div className="timestamp-item">
							<Icon icon="Update" className="text-muted me-2" />
							<span className="text-muted">Updated: {new Date(location.updatedAt).toLocaleString()}</span>
						</div>
					</div> */}
				</div>

				<style>{`
					.location-details-container {
						overflow: hidden;
					}

					.location-header {
						padding: 2rem;
						position: relative;
						overflow: hidden;
					}

					.location-header::before {
						content: '';
						position: absolute;
						top: 0;
						right: 0;
						width: 200px;
						height: 200px;
						background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
						border-radius: 50%;
						transform: translate(50%, -50%);
					}

					.location-header-content {
						display: flex;
						justify-content: space-between;
						align-items: start;
						position: relative;
						z-index: 1;
					}

					.location-badge {
						display: inline-flex;
						align-items: center;
						padding: 0.5rem 1rem;
						border-radius: 25px;
						font-size: 0.875rem;
						font-weight: 600;
						margin-bottom: 1rem;
					}

					.location-name {
						font-size: 2rem;
						font-weight: 700;
						margin: 0;
					}

					.location-code {
						display: flex;
						align-items: center;
						font-family: 'Courier New', monospace;
						font-size: 0.95rem;
						padding: 0.25rem 0.75rem;
						border-radius: 15px;
						width: fit-content;
					}

					.status-indicator {
						display: flex;
						align-items: center;
						gap: 0.5rem;
						padding: 0.75rem 1.25rem;
						border-radius: 25px;
						font-weight: 600;
						font-size: 0.875rem;
					}

					.status-dot {
						width: 8px;
						height: 8px;
						border-radius: 50%;
						background: currentColor;
						animation: pulse 2s infinite;
					}

					@keyframes pulse {
						0% { opacity: 1; }
						50% { opacity: 0.5; }
						100% { opacity: 1; }
					}

					.location-details-grid {
						display: grid;
						grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
						gap: 1.5rem;
					}

					.detail-card {
						border-radius: 16px;
						padding: 1.5rem;
						box-shadow: 0 2px 10px rgba(0,0,0,0.1);
						transition: all 0.3s ease;
						position: relative;
						overflow: hidden;
					}

					.detail-card::before {
						content: '';
						position: absolute;
						top: 0;
						left: 0;
						width: 100%;
						height: 3px;
						background: var(--bs-primary);
					}

					.detail-card:hover {
						transform: translateY(-2px);
						box-shadow: 0 4px 20px rgba(0,0,0,0.15);
					}

					.detail-card-header {
						display: flex;
						align-items: center;
						gap: 0.75rem;
						margin-bottom: 1.25rem;
						padding-bottom: 0.75rem;
						font-weight: 600;
					}

					.detail-items {
						display: flex;
						flex-direction: column;
						gap: 1rem;
					}

					.detail-item {
						display: flex;
						flex-direction: column;
						gap: 0.25rem;
					}

					.detail-label {
						font-size: 0.875rem;
						font-weight: 500;
						text-transform: uppercase;
						letter-spacing: 0.5px;
					}

					.detail-value {
						font-weight: 600;
						word-break: break-word;
					}

					.balance-amount {
						font-size: 1.25rem;
						padding: 0.5rem 1rem;
						background: linear-gradient(135deg, #e8f5e8 0%, #f0fff0 100%);
						border-radius: 12px;
						border: 2px solid #28a745;
						display: inline-block;
						min-width: 150px;
						text-align: center;
						box-shadow: 0 2px 8px rgba(40, 167, 69, 0.15);
						transition: all 0.3s ease;
					}

					.balance-amount:hover {
						transform: translateY(-1px);
						box-shadow: 0 4px 12px rgba(40, 167, 69, 0.25);
					}

					.location-timestamps {
						display: flex;
						flex-wrap: wrap;
						gap: 2rem;
						font-size: 0.875rem;
					}

					.timestamp-item {
						display: flex;
						align-items: center;
					}

					@media (max-width: 768px) {
						.location-header-content {
							flex-direction: column;
							gap: 1rem;
						}

						.location-name {
							font-size: 1.5rem;
						}

						.location-details-grid {
							grid-template-columns: 1fr;
							gap: 1rem;
						}

						.detail-card {
							padding: 1rem;
						}

						.location-timestamps {
							flex-direction: column;
							gap: 1rem;
						}
					}
				`}</style>
			</ModalBody>
			<ModalFooter>
				<Button color='secondary' onClick={() => setIsOpen(false)}>
					Close
				</Button>
			</ModalFooter>
		</Modal>
	);
};

export default LocationDetailsModal;
